"use client"

import type { CSSProperties } from "react"

import { cn } from "@productlane/lib/src/browser"

export interface ChangelogTimelineDotProps {
	className?: string
	style?: CSSProperties
	color?: string
}

export function ChangelogTimelineDot({
	className,
	style,
	color,
}: ChangelogTimelineDotProps) {
	return (
		<span
			className={cn(
				"absolute z-10 h-3.5 w-3.5 -translate-x-1/2 rounded-full bg-white shadow ring-1 ring-gray-900/10 dark:bg-gray-800 dark:ring-white/10",
				className,
			)}
			style={style}
			aria-hidden="true"
		>
			{/* Centering lives on this inner wrapper, never on the outer span:
			    tailwind-merge drops a `flex` there in favor of the caller's
			    `hidden md:block` (same `display` property), and the
			    absolute+inset+margin-auto alternative renders off-center in
			    Safari. */}
			<span className="flex h-full w-full items-center justify-center">
				<span
					className="h-1.5 w-1.5 rounded-full"
					style={{ backgroundColor: color ?? "var(--portal-accent-color)" }}
				/>
			</span>
		</span>
	)
}

export interface ChangelogTimelineLineProps {
	isFirst?: boolean
	isLast?: boolean
	className?: string
	style?: CSSProperties
}

export function ChangelogTimelineLine({
	isFirst = false,
	isLast = false,
	className,
	style,
}: ChangelogTimelineLineProps) {
	return (
		<span
			className={cn(
				"absolute bottom-0 w-[0.5px] -translate-x-1/2",
				isLast
					? "bg-gradient-to-b from-gray-300 to-transparent dark:from-gray-700"
					: "bg-gray-300 dark:bg-gray-700",
				className,
			)}
			style={{
				top: isFirst ? "1.5rem" : "0",
				...style,
			}}
			aria-hidden="true"
		/>
	)
}

export interface ChangelogTimelineProps {
	isFirst?: boolean
	isLast?: boolean
	showDot?: boolean
	color?: string
	className?: string
}

/**
 * Self-contained timeline visual: a vertical line with an optional colored dot at the top.
 * Renders absolutely inside its parent — wrap in a `relative` container that defines its bounds.
 */
export function ChangelogTimeline({
	isFirst = false,
	isLast = false,
	showDot = true,
	color,
	className,
}: ChangelogTimelineProps) {
	return (
		<div
			className={cn("pointer-events-none absolute inset-0", className)}
			aria-hidden="true"
		>
			{showDot && (
				<ChangelogTimelineDot className="left-1/2 top-2" color={color} />
			)}
			<ChangelogTimelineLine
				isFirst={isFirst && showDot}
				isLast={isLast}
				className="left-1/2"
			/>
		</div>
	)
}
